import React, { useEffect, useState } from "react";
import { Box, Button, Checkbox, Chip, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle,
  FormControlLabel, Grid, MenuItem, Paper, TextField, Typography } from "@material-ui/core";
import Alert from "@material-ui/lab/Alert";
import Autocomplete from "@material-ui/lab/Autocomplete";
import api from "../../services/api";

const display = value => value && typeof value === "object"
  ? `${value.name} (${value.code})${value.department ? ` - ${value.department.name}` : ""}` : String(value ?? "Sin dato");
const resultLabels = { verified: "Confirmado", review_required: "Requiere revision", not_applied: "No enviado", executing: "En proceso", preview: "Solo revision" };

export default function CustomerProfileDialog({ customer, onClose, onSaved }) {
  const [data, setData] = useState(null);
  const [selected, setSelected] = useState([]);
  const [referenceId, setReferenceId] = useState("");
  const [patch, setPatch] = useState({});
  const [reason, setReason] = useState("");
  const [preview, setPreview] = useState(null);
  const [result, setResult] = useState(null);
  const [history, setHistory] = useState(null);
  const [busy, setBusy] = useState(true);
  const [error, setError] = useState("");
  const base = `/clients/${customer.id}/customer-profile`;
  useEffect(() => {
    let active = true;
    api.get(base).then(({ data: response }) => {
      if (!active) return;
      setData(response);
      setReferenceId(response.sources.find(s => s.present)?.sourceId || "");
      setSelected(response.sources.filter(s => s.present && s.writable).map(s => s.sourceId));
    }).catch(() => { if (active) setError("No se pudo consultar la edicion sincronizada. No se ha cambiado ningun dato."); })
      .finally(() => { if (active) setBusy(false); });
    return () => { active = false; };
  }, [base]);
  const run = async action => {
    setBusy(true); setError("");
    try { await action(); } catch (e) { setError(e.response?.data?.message || "No se confirmo la operacion. Revise el historial antes de repetir."); }
    finally { setBusy(false); }
  };
  const reference = data?.sources.find(s => s.sourceId === Number(referenceId) && s.present);
  const locked = busy || Boolean(preview) || Boolean(result);
  const edit = (key, value) => setPatch(old => ({ ...old, [key]: value }));
  const sources = data?.sources || [];
  return <Dialog open fullWidth maxWidth="lg" onClose={() => !busy && onClose()}>
    <DialogTitle>Editar y sincronizar con APIs: {customer.name}</DialogTitle>
    <DialogContent dividers>
      <Alert severity="info">Seleccione los datos que desea corregir. Se enviaran a las APIs marcadas donde ya existe el cliente, sin crear registros ni alterar documentos emitidos. NIT, claves, planes y numeracion se gestionan por separado.</Alert>
      {error && <Box mt={2}><Alert severity="error">{error}</Alert></Box>}
      {busy && <Box p={2}><CircularProgress size={24} /> Consultando o verificando...</Box>}
      <Box my={2}><Typography variant="h6">1. Destinos del cambio</Typography>
        <Typography variant="body2" color="textSecondary">Todos los destinos habilitados se seleccionan inicialmente. Revise las APIs no disponibles: no se puede afirmar que el cambio llegue a todas si alguna queda pendiente.</Typography></Box>
      <Grid container spacing={2}>{sources.map(source => <Grid item xs={12} md={4} key={source.sourceId}>
        <Paper variant="outlined"><Box p={2}>
          <FormControlLabel control={<Checkbox color="primary" checked={selected.includes(source.sourceId)}
            disabled={locked || !source.present || !source.writable}
            onChange={(_, checked) => { setSelected(old => checked ? [...old, source.sourceId] : old.filter(id => id !== source.sourceId)); setPatch({}); }} />} label={source.name} />
          <Chip size="small" label={source.status === "unavailable" ? "Sin verificar" : !source.present ? "No registrado" : source.writable ? "Disponible" : "Pendiente de habilitar"} />
          {source.message && <Typography variant="body2">{source.message}</Typography>}
          {source.present && <Box mt={1}><Typography variant="body2">{display(source.profile.address)}</Typography>
            <Typography variant="body2">{display(source.profile.municipality_id)}</Typography>
            <Typography variant="body2">{display(source.profile.type_regime_id)}</Typography></Box>}
        </Box></Paper>
      </Grid>)}</Grid>
      {reference && !result && <Box mt={3}>
        <Typography variant="h6">2. Datos a corregir</Typography>
        <Box my={2}><TextField id="customer-profile-reference" select fullWidth variant="outlined" label="Ver datos actuales de"
          value={referenceId} disabled={locked} onChange={event => { setReferenceId(event.target.value); setPatch({}); }}>
          {sources.filter(source => source.present).map(source => <MenuItem key={source.sourceId} value={source.sourceId}>{source.name}</MenuItem>)}
        </TextField></Box>
        {!reference.writable && <Box mb={2}><Alert severity="warning">Consulta disponible. El guardado en esta API aun esta deshabilitado.</Alert></Box>}
        <Typography variant="body2" color="textSecondary">Solo se guardan los campos marcados. Los valores iniciales corresponden a {reference.name}; la revision mostrara las diferencias por API.</Typography>
        <Grid container spacing={2}>{Object.entries(data.fields).map(([key, label]) => {
          const enabled = Object.prototype.hasOwnProperty.call(patch, key);
          const options = reference.catalogs?.[key];
          return <Grid item xs={12} md={6} key={key}>
            <FormControlLabel label={`Cambiar ${label}`} control={<Checkbox color="primary" checked={enabled} disabled={locked || !reference.writable}
              onChange={(_, checked) => setPatch(old => { const next = { ...old }; if (checked) next[key] = reference.profile[key] ?? ""; else delete next[key]; return next; })} />} />
            {options ? <Autocomplete options={options} value={(enabled ? patch[key] : reference.profile[key]) || null}
              disabled={locked || !enabled} getOptionLabel={display} getOptionSelected={(a, b) => a.code === b.code}
              onChange={(_, value) => edit(key, value)} renderInput={params => <TextField {...params} label={label} variant="outlined" size="small" />} />
              : <TextField id={`customer-profile-${key}`} fullWidth label={label} variant="outlined" size="small" disabled={locked || !enabled}
                value={(enabled ? patch[key] : reference.profile[key]) ?? ""} onChange={e => edit(key, e.target.value)} />}
          </Grid>;
        })}</Grid>
        <Box mt={2}><TextField id="customer-profile-reason" fullWidth label="Motivo del cambio" value={reason} disabled={locked} variant="outlined"
          helperText="Quedara registrado junto con su usuario y los valores anteriores." onChange={e => setReason(e.target.value)} /></Box>
      </Box>}
      {preview && !result && <Box mt={3}>
        <Typography variant="h6">3. Revise antes de confirmar</Typography>
        <Alert severity="warning">{preview.notice}</Alert>
        {preview.targets.map(target => <Box key={target.sourceId} mt={2}><Typography variant="subtitle1">{target.name}</Typography>
          {Object.keys(preview.patch).map(key => <Typography key={key} variant="body2">
            <strong>{data.fields[key]}:</strong> {display(target.before[key])} → {display(target.after[key])}
          </Typography>)}
        </Box>)}
      </Box>}
      {result && <Box mt={3}><Alert severity={result.success ? "success" : "warning"}>
        {result.success ? "Cambios confirmados en los destinos seleccionados." : "Hay resultados pendientes. No se confirmo el cambio en todas las APIs."}</Alert>
        {result.results.map(item => <Box mt={1} key={item.sourceId}><Typography><strong>{item.name}:</strong> {resultLabels[item.status]}</Typography>
          {item.message && <Typography variant="body2">{item.message}</Typography>}</Box>)}
      </Box>}
      {history && <Box mt={3}><Typography variant="h6">Historial reciente</Typography>
        {!history.length && <Typography>No hay operaciones registradas.</Typography>}
        {history.map(item => <Box key={item.id} my={1}><Typography>#{item.id} · Usuario {item.userId} · {new Date(item.at).toLocaleString("es-CO")} · {resultLabels[item.status] || item.status}</Typography>
          <Typography variant="body2">{item.reason}</Typography>
          {(item.results?.results || []).map(target => <Typography key={target.sourceId} variant="body2">{target.name}: {resultLabels[target.status]}</Typography>)}
        </Box>)}
      </Box>}
    </DialogContent>
    <DialogActions>
      <Button disabled={busy} onClick={() => run(async () => { const { data } = await api.get(`${base}/history`); setHistory(data.history); })}>Ver historial</Button>
      <Button disabled={busy} onClick={onClose}>Cerrar</Button>
      {!preview && !result && <Button variant="contained" color="primary" disabled={busy || !selected.length || !Object.keys(patch).length || reason.trim().length < 5}
        onClick={() => run(async () => { const { data } = await api.post(`${base}/preview`, { sourceIds: selected, patch, reason }); setPreview(data); })}>Revisar cambios</Button>}
      {preview && !result && <><Button disabled={busy} onClick={() => setPreview(null)}>Volver a editar</Button>
        <Button variant="contained" color="primary" disabled={busy} onClick={() => run(async () => {
          // A failed response may follow a successful remote write. Consume the
          // local confirmation immediately; retries require inspecting history.
          const id = preview.previewId; setPreview(null); setResult({ success: false, results: [] });
          const { data } = await api.post(`${base}/confirm`, { previewId: id, confirm: true }); setResult(data); if (onSaved) onSaved();
        })}>Confirmar y sincronizar</Button></>}
    </DialogActions>
  </Dialog>;
}
